import { useEffect, useState, type FormEvent } from 'react'
import { useAkuteStatus, useJoinPelviaCareWaitlist } from '@pelvia/shared/hooks/patient/useAkute'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { captureEvent } from '@/lib/analytics'

/**
 * PelviaCare+ "coming soon" card.
 *
 * Shown on the patient homepage in place of the paid consult-note flow whenever
 * the PelviaCare+ master switch is off (server `Config.PELVIACARE_ENABLED`,
 * surfaced as `available` on usePelviaCare). It teases the product with a bit of
 * motion and lets the patient join the early-access waitlist in one step — the
 * same LaunchList list the marketing site uses (form id `a13QQx`).
 *
 * Remembering a prior signup: LaunchList gives us no way to read its list back,
 * so on join we (a) post to LaunchList (the real list) and (b) record the join
 * against the account server-side. The card then shows the confirmed state from
 * either the server flag (`waitlist_joined`, so it survives across devices) or
 * localStorage (an instant, offline-safe fallback on the same browser).
 */

// The PelviaCare+ early-access list (shared with the landing page waitlist).
const WAITLIST_ENDPOINT = 'https://getlaunchlist.com/s/a13QQx'
const JOINED_KEY = 'pelvia_pelviacare_waitlist_joined'
// Public landing waitlist section — the fallback if the inline post can't reach
// LaunchList (e.g. offline), so the patient always has a way through.
const WAITLIST_FALLBACK_URL = '/#app-waitlist'

function alreadyJoined(): boolean {
  try {
    return localStorage.getItem(JOINED_KEY) === '1'
  } catch {
    return false
  }
}

function rememberJoined(): void {
  try {
    localStorage.setItem(JOINED_KEY, '1')
  } catch {
    /* private mode / quota — the confirmed state just won't persist */
  }
}

export default function PelviaCareComingSoon() {
  const { data: user } = useCurrentUser()
  const { data: akute } = useAkuteStatus()
  const joinWaitlist = useJoinPelviaCareWaitlist()
  const [email, setEmail] = useState('')
  // Seed from localStorage for an instant confirmed state on this browser; the
  // server flag (below) is the cross-device source of truth once it loads.
  const [joined, setJoined] = useState<boolean>(alreadyJoined)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Flip to confirmed once the account is known to be on the waitlist, e.g. a
  // signup made on another device or before this browser's storage was cleared.
  useEffect(() => {
    if (akute?.waitlist_joined) setJoined(true)
  }, [akute?.waitlist_joined])

  // Prefill with the signed-in email the first time the field renders, but let
  // the patient override it (they may want a personal inbox on the list).
  const emailValue = email || user?.email || ''

  async function join(e: FormEvent) {
    e.preventDefault()
    const value = emailValue.trim()
    if (!value || !value.includes('@')) {
      setError('Enter a valid email so we can reach you.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      // no-cors: LaunchList doesn't send back permissive CORS headers, so we
      // can't read the response — but the form-encoded POST still registers the
      // signup. Treat a resolved request as success (optimistic).
      await fetch(WAITLIST_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: value }).toString(),
      })
      rememberJoined()
      setJoined(true)
      // Persist the join against the account so the confirmed state shows on any
      // device. Fire-and-forget: localStorage already covers this browser, and a
      // failure here shouldn't block the (already-registered) LaunchList signup.
      joinWaitlist.mutate()
      captureEvent('pelviacare_waitlist_joined', { source: 'coming_soon_card' })
    } catch {
      setError(null)
      // Network-level failure — send them to the full waitlist page instead.
      window.open(WAITLIST_FALLBACK_URL, '_blank', 'noopener')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="card pp-report-cta pp-coming-card">
      {/* Decorative animated aurora — purely visual. */}
      <div className="pp-coming-aura" aria-hidden />

      <div className="pp-coming-badge-row">
        <span className="pp-coming-badge">
          <span className="pp-coming-badge-dot" aria-hidden />
          PelviaCare+
        </span>
        <span className="pp-coming-chip">Coming soon</span>
      </div>

      <div className="pp-coming-body">
        <strong className="pp-coming-title">
          Your path to a signed diagnosis is <em>almost here.</em>
        </strong>
        <p className="pp-coming-desc">
          PelviaCare+ turns your Pelvia history into a board-certified physician
          sign-off and an excision-surgeon referral, in 72 hours instead of
          seven years. We&rsquo;re opening it to a small group first.
        </p>

        <ul className="pp-report-benefits pp-coming-benefits">
          <li>Clinician review of your full symptom &amp; pattern history</li>
          <li>A signed consult note for your insurance or referral</li>
          <li>Your next step: OB/GYN eval or surgery consult</li>
        </ul>

        {joined ? (
          <div className="pp-coming-joined" role="status">
            <span className="pp-coming-joined-check" aria-hidden>
              ✓
            </span>
            <div>
              <strong>You&rsquo;re on the waitlist.</strong>
              <span> We&rsquo;ll email you the moment early access opens.</span>
            </div>
          </div>
        ) : (
          <form className="pp-coming-form" onSubmit={join}>
            <div className="pp-coming-input-group">
              <input
                type="email"
                name="email"
                autoComplete="email"
                placeholder="you@email.com"
                value={emailValue}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Email for the PelviaCare+ waitlist"
                required
              />
              <button type="submit" className="pp-report-btn" disabled={submitting}>
                {submitting ? 'Joining…' : 'Join the waitlist'}
              </button>
            </div>
            {error && <p className="pp-coming-error">{error}</p>}
            <p className="pp-coming-note">
              Free to join. No spam. You&rsquo;ll be first to know when we open access.
            </p>
          </form>
        )}
      </div>
    </section>
  )
}
